import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, Loader2, Store, CheckCircle2 } from "lucide-react";
import {
  createHireRequest,
  currencyForCountry,
  SUPPORTED_CURRENCIES,
  PERFORMER_TYPE_OPTIONS,
  listMyLocations,
} from "@/business/api";

const HOURS = (() => {
  const a = [];
  for (let h = 0; h < 24; h++) {
    a.push(`${String(h).padStart(2, "0")}:00`);
    a.push(`${String(h).padStart(2, "0")}:30`);
  }
  return a;
})();

const DURATION_OPTIONS = [
  "30 minutes",
  "1 hour",
  "1.5 hours",
  "2 hours",
  "3 hours",
  "4 hours",
  "5 hours",
  "6 hours",
  "All day",
];

function formatDateISO(d) {
  if (!d) return "";
  return d.toISOString().slice(0, 10);
}
function formatDateDisplay(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function venueDisplayLocation(v) {
  if (!v) return "";
  return [v.address, v.city].filter(Boolean).join(", ");
}

/**
 * <HireBuskerForm>
 *
 * Shared form used by both:
 *   - /business/dashboard/hire (business owner posts a gig from their dashboard)
 *   - /business/{username} (when viewed by the owner business — same form, opens in a Sheet)
 *
 * Multi-venue behavior:
 *   - If `locations` prop is provided (array), render a "Posting for venue:"
 *     dropdown above the form. Default selection = is_primary venue, or the
 *     first item.
 *   - If `locations` is not provided, the form fetches GET /api/business/my-locations
 *     itself when `business` is an authenticated owner. Anonymous public pages
 *     pass `locations={null}` and the picker is hidden.
 *   - On submit, the selected venue's id is sent as `business_location_id`
 *     in the POST body to /api/hire-requests.
 *
 * Props:
 *   business: business profile object (from GET /api/auth/business/me or
 *             the public profile endpoint). Used to prefill defaults when no
 *             venue picker is active.
 *   locations: optional array of {id, business_name, city, address, country, is_primary}
 *              to populate the venue picker. Pass `null` (or omit) to hide picker.
 *   autoLoadLocations: when true (default in dashboard) and no `locations`
 *              prop is given, fetch them from the API and show the picker.
 *   onSuccess(result): called with the API response after a successful post.
 *   onUnauthorized(): called when the API returns 401 (token invalid/expired).
 *   submitLabel: optional, defaults to "Post gig".
 *   className: optional wrapper className.
 */
export default function HireBuskerForm({
  business,
  locations: locationsProp,
  autoLoadLocations = false,
  preferredLocationId,
  onSuccess,
  onUnauthorized,
  submitLabel = "Post gig",
  className = "",
}) {
  const [locations, setLocations] = useState(
    Array.isArray(locationsProp) ? locationsProp : null
  );
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedVenueId, setSelectedVenueId] = useState("");

  // Auto-load venues when permitted and no list was passed in.
  useEffect(() => {
    if (Array.isArray(locationsProp)) {
      setLocations(locationsProp);
      return;
    }
    if (!autoLoadLocations) return;
    let cancelled = false;
    (async () => {
      setLocationsLoading(true);
      try {
        const list = await listMyLocations();
        // Backend returns { total, items: [...] } — also tolerate plain
        // array or { locations: [...] } shapes.
        const arr = Array.isArray(list)
          ? list
          : list?.items || list?.locations || [];
        if (!cancelled) setLocations(arr);
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          onUnauthorized?.();
          return;
        }
        // Fail soft — fall back to single-venue behavior from `business`.
        if (!cancelled) setLocations([]);
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadLocations]);

  // Resolve selected venue & defaults.
  const selectedVenue = useMemo(() => {
    if (!Array.isArray(locations) || !locations.length) return null;
    if (selectedVenueId) {
      return locations.find((v) => v.id === selectedVenueId) || null;
    }
    return locations.find((v) => v.is_primary) || locations[0];
  }, [locations, selectedVenueId]);

  // When locations arrive, seed selection to the preferred venue (if any),
  // otherwise the primary venue, otherwise the first item.
  useEffect(() => {
    if (!Array.isArray(locations) || !locations.length) return;
    if (selectedVenueId) return;
    let target = null;
    if (preferredLocationId) {
      target = locations.find((v) => v.id === preferredLocationId) || null;
    }
    if (!target) target = locations.find((v) => v.is_primary) || locations[0];
    if (target) setSelectedVenueId(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, preferredLocationId]);

  // ---- Prefill source (venue-aware when present) ----
  const sourceForDefaults = selectedVenue || business || {};
  const defaultLocation = useMemo(() => {
    const fromVenue = venueDisplayLocation(selectedVenue);
    if (fromVenue) return fromVenue;
    return [sourceForDefaults?.address, sourceForDefaults?.city]
      .filter(Boolean)
      .join(", ");
  }, [selectedVenue, sourceForDefaults]);
  const defaultCurrency = useMemo(
    () => currencyForCountry(sourceForDefaults?.country),
    [sourceForDefaults]
  );
  const defaultEmail =
    selectedVenue?.business_email || business?.business_email || business?.email || "";

  const [date, setDate] = useState(null);
  const [form, setForm] = useState({
    event_type: "",
    performer_type: "jazz",
    location: defaultLocation,
    event_time: "19:00",
    duration: "2 hours",
    budget: "",
    currency: defaultCurrency,
    description: "",
    equipment_provided: true,
    equipment_details: "",
    contact_email: defaultEmail,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Track which fields have been user-touched, so switching venue only
  // overwrites fields the user hasn't customised.
  const [touched, setTouched] = useState({});

  // Resync location/currency/email whenever the selected venue or business changes,
  // but only for fields the user hasn't manually edited.
  useEffect(() => {
    setForm((s) => ({
      ...s,
      location: touched.location ? s.location : defaultLocation,
      currency: touched.currency ? s.currency : defaultCurrency,
      contact_email: touched.contact_email ? s.contact_email : defaultEmail,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLocation, defaultCurrency, defaultEmail]);

  const upd = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setTouched((t) => ({ ...t, [k]: true }));
    setForm((s) => ({ ...s, [k]: v }));
  };

  const validate = () => {
    if (!form.event_type.trim())
      return 'Please give the gig a title (e.g. "Live Jazz Friday").';
    if (!date) return "Please pick a date.";
    if (!form.location.trim()) return "Please add a location.";
    if (form.budget !== "" && Number.isNaN(Number(form.budget)))
      return "Budget must be a number.";
    if (showVenuePicker && !selectedVenue?.id)
      return "Please select which venue this gig is for.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    const payload = {
      request_type: "general",
      event_type: form.event_type.trim(),
      performer_type: form.performer_type,
      location: form.location.trim(),
      event_date: formatDateISO(date),
      event_date_display: formatDateDisplay(date),
      event_time: form.event_time,
      duration: form.duration,
      currency: form.currency || "USD",
      description: form.description.trim(),
      equipment_provided: !!form.equipment_provided,
      equipment_details: form.equipment_provided
        ? (form.equipment_details || "").trim()
        : "",
    };
    if (form.budget !== "" && form.budget !== null) {
      const b = Number(form.budget);
      if (Number.isFinite(b)) payload.budget = b;
    }
    if (selectedVenue?.id) {
      payload.business_location_id = selectedVenue.id;
    }

    setSubmitting(true);
    try {
      const result = await createHireRequest(payload);
      toast.success("Gig posted! Buskers can now apply.");
      onSuccess?.(result);
    } catch (e2) {
      if (e2.status === 401 || e2.status === 403) {
        toast.error("Your session expired. Please sign in again.");
        onUnauthorized?.();
        return;
      }
      // Surface backend error verbatim (e.g. "Selected venue does not belong
      // to this owner.") so the owner sees exactly what went wrong.
      setErr(e2?.message || "Could not post gig. Please try again.");
      toast.error(e2?.message || "Could not post gig.");
    } finally {
      setSubmitting(false);
    }
  };

  const showVenuePicker = Array.isArray(locations) && locations.length > 0;

  return (
    <form
      onSubmit={onSubmit}
      className={`space-y-4 ${className}`}
      data-testid="hire-busker-form"
      noValidate
    >
      {/* Venue picker (dashboard / owner only) */}
      {locationsLoading && !showVenuePicker && (
        <div
          className="flex items-center gap-2 text-sm text-[#6B7280]"
          data-testid="hire-form-venues-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your venues…
        </div>
      )}

      {showVenuePicker && (
        <div
          className="rounded-lg border border-[#FFD7BF] bg-[#FFF8F2] p-3"
          data-testid="hire-form-venue-picker"
        >
          <Label
            htmlFor="business_location_id"
            className="flex items-center gap-1 mb-1.5 text-[#111827]"
          >
            <Store className="h-3.5 w-3.5 text-[#FF6A00]" /> Posting for venue
          </Label>
          <Select
            value={selectedVenue?.id || ""}
            onValueChange={(v) => {
              setSelectedVenueId(v);
              // Allow auto-prefill again for fields the user hasn't touched
              // (handled by `touched` map — venue switch should overwrite
              // untouched fields, which already happens via the effect).
            }}
          >
            <SelectTrigger
              id="business_location_id"
              data-testid="hire-form-venue-select"
              className="bg-white"
            >
              <SelectValue placeholder="Select a venue" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((v) => (
                <SelectItem
                  key={v.id}
                  value={v.id}
                  data-testid={`hire-form-venue-option-${v.id}`}
                >
                  <span className="inline-flex items-center gap-2">
                    {v.business_name}
                    {v.city ? (
                      <span className="text-[#6B7280] text-xs">• {v.city}</span>
                    ) : null}
                    {v.is_primary ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#B54708]">
                        <CheckCircle2 className="h-3 w-3" /> Primary
                      </span>
                    ) : null}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-1.5 text-[11px] text-[#6B7280]">
            Location, country and currency below are auto-filled from this venue.
            You can still edit any field.
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="event_type">
          Gig title <span className="text-[#DC2626]">*</span>
        </Label>
        <Input
          id="event_type"
          value={form.event_type}
          onChange={upd("event_type")}
          required
          maxLength={120}
          placeholder='e.g. "Live Jazz Friday"'
          data-testid="hire-form-title-input"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label>
            Date <span className="text-[#DC2626]">*</span>
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start font-normal"
                data-testid="hire-form-date-button"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? formatDateDisplay(date) : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label htmlFor="event_time">Start time</Label>
          <Select
            value={form.event_time}
            onValueChange={(v) => {
              setTouched((t) => ({ ...t, event_time: true }));
              setForm((s) => ({ ...s, event_time: v }));
            }}
          >
            <SelectTrigger id="event_time" data-testid="hire-form-time-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {HOURS.map((h) => (
                <SelectItem key={h} value={h}>
                  {h}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="duration">Duration</Label>
          <Select
            value={form.duration}
            onValueChange={(v) => {
              setTouched((t) => ({ ...t, duration: true }));
              setForm((s) => ({ ...s, duration: v }));
            }}
          >
            <SelectTrigger id="duration" data-testid="hire-form-duration-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DURATION_OPTIONS.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="performer_type">Performer type</Label>
        <Select
          value={form.performer_type}
          onValueChange={(v) => {
            setTouched((t) => ({ ...t, performer_type: true }));
            setForm((s) => ({ ...s, performer_type: v }));
          }}
        >
          <SelectTrigger
            id="performer_type"
            data-testid="hire-form-performer-type-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERFORMER_TYPE_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="location">
          Location <span className="text-[#DC2626]">*</span>
        </Label>
        <Input
          id="location"
          value={form.location}
          onChange={upd("location")}
          required
          placeholder="Venue name and address"
          data-testid="hire-form-location-input"
        />
      </div>

      <div className="grid grid-cols-[1fr_120px] gap-3">
        <div>
          <Label htmlFor="budget">Budget</Label>
          <Input
            id="budget"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={form.budget}
            onChange={upd("budget")}
            placeholder="e.g. 300"
            data-testid="hire-form-budget-input"
          />
        </div>
        <div>
          <Label htmlFor="currency">Currency</Label>
          <Select
            value={form.currency}
            onValueChange={(v) => {
              setTouched((t) => ({ ...t, currency: true }));
              setForm((s) => ({ ...s, currency: v }));
            }}
          >
            <SelectTrigger id="currency" data-testid="hire-form-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {SUPPORTED_CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <label
        className="flex items-center justify-between gap-3 rounded-lg border border-[#E5E7EB] p-3"
        data-testid="hire-form-equipment-row"
      >
        <div>
          <div className="text-sm font-medium">Equipment provided</div>
          <div className="text-xs text-[#6B7280]">
            PA, mics, mixer — what the busker can use on arrival.
          </div>
        </div>
        <Switch
          checked={!!form.equipment_provided}
          onCheckedChange={(v) =>
            setForm((s) => ({ ...s, equipment_provided: v }))
          }
          data-testid="hire-form-equipment-switch"
        />
      </label>
      {form.equipment_provided && (
        <div>
          <Label htmlFor="equipment_details">Equipment details</Label>
          <Input
            id="equipment_details"
            value={form.equipment_details}
            onChange={upd("equipment_details")}
            placeholder="e.g. PA system, 2 mics, mixer"
            data-testid="hire-form-equipment-details-input"
          />
        </div>
      )}

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={form.description}
          onChange={upd("description")}
          rows={4}
          placeholder="Style, vibe, audience size, expectations."
          data-testid="hire-form-description-input"
        />
      </div>

      {err && (
        <div
          role="alert"
          data-testid="hire-form-error-text"
          className="text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md px-3 py-2"
        >
          {err}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          className="bg-[#FF6A00] hover:bg-[#E85F00] text-white"
          data-testid="hire-form-submit-button"
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{" "}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
